import { formatRoute } from '@visious/core';
import type { WatchTarget } from '../screens/watch/useWatchScreen';

/**
 * The decisions the playback host makes that are not React (R13), so `PlaybackHost.tsx` renders and
 * this file reasons.
 */

/**
 * Whether two watch routes are the *same playback*, which is a narrower question than "same video".
 *
 * It has to be, and the reason is a real failure rather than tidiness. `useWatchScreen` re-runs its
 * player effect when `startAt` changes, and `startAt` comes from the route's `t`. So a Maximize that
 * navigated to a bare `/watch?v=…` while the retained session carried `?t=90` would look like an
 * unchanged video and behave like a reload: the manifest is refetched and the picture jumps back to
 * 1:30, in the one flow whose whole promise is that playback is not interrupted. Every field the
 * route can carry is compared, and `maximizeHref` below rebuilds all of them.
 */
export function sameWatchTarget(a: WatchTarget | null, b: WatchTarget | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.videoId === b.videoId &&
    a.startSec === b.startSec &&
    a.playlistId === b.playlistId &&
    a.index === b.index
  );
}

/** The route the minimized player goes back to — the retained target, spelled out in full. */
export function maximizeHref(target: WatchTarget): string {
  return formatRoute({
    kind: 'watch',
    videoId: target.videoId,
    startSec: target.startSec,
    playlistId: target.playlistId,
    index: target.index,
  });
}

/** What the corner player is on off `/watch`, and what is left of the retained session behind it. */
export interface OffRouteTarget {
  readonly target: WatchTarget | null;
  /** The session to keep. `null` retires it, which is a state change for the host to make. */
  readonly session: WatchTarget | null;
}

/**
 * Which target the player should be on while the reader is **off** `/watch`.
 *
 * Two things can claim the corner player and they claim it for different lengths of time. The
 * *retained session* is the route the reader just left, and it exists so that walking away from a
 * playing video does not interrupt it. The *queue* is a list this tab is watching through, and it
 * exists until the reader empties it. Both are `WatchTarget`s; the question is only ever which one
 * the corner is showing right now, and there are exactly four answers:
 *
 * - **Only one of them exists** — that one, trivially.
 * - **They name the same video.** The retained session wins, and this is the arm that has a measured
 *   failure behind it rather than a preference. A queue entry carries no `t`, so a reader who left
 *   `/watch?v=X&t=90` would otherwise have `startAt` change from 90 to `null` under an unchanged
 *   video id — no re-key, so no remount, but `useWatchScreen`'s player effect depends on `startAt`
 *   and would reload the manifest and drop the picture back to the start. The richer target is the
 *   safe one, and it describes the same playback either way.
 * - **The queue has dropped the video the session was playing from it.** The session is then a
 *   record of a row that no longer exists, so it is retired and the cursor takes the corner. This is
 *   the arm the others cannot cover: a removal is the one edit that moves the cursor by taking the
 *   playing entry *out*, so "the session's video is no longer in the list" arrives as the answer to
 *   both "was it removed" and "was it never queued", and only the caller's `playingFromQueue`
 *   separates them.
 * - **They name different videos.** The queue wins *if it is what was playing* — which is exactly
 *   what an advance looks like from here: the cursor moved on, so the retained session now names the
 *   video that just ended. If the retained session's video is not in the queue at all, the reader is
 *   watching something off-queue and carried it out of the page with them; that keeps the corner.
 *
 * **A session the queue has taken over is retired rather than parked**, in both of the arms where
 * the queue wins. Keeping it would leave the corner something to fall back to when the queue stops
 * — and the queue stops at the end of the list, so the last video of a queue would be followed by
 * its first one playing again, which is the one thing `useQueueAdvance` says an ending must not do.
 */
export function offRouteTarget(input: {
  readonly session: WatchTarget | null;
  readonly queue: WatchTarget | null;
  /** Whether the queue holds this video anywhere in it, at any position. */
  readonly queueHolds: (videoId: string) => boolean;
  /**
   * The video the session was playing **from the queue** as of the previous render, if it was. What
   * tells a row that has been removed from under the corner player apart from a video that was never
   * in the list at all.
   */
  readonly playingFromQueue: string | null;
}): OffRouteTarget {
  const { session, queue, queueHolds, playingFromQueue } = input;
  if (session === null) return { target: queue, session: null };
  const dropped = playingFromQueue === session.videoId && !queueHolds(session.videoId);
  if (dropped) return { target: queue, session: null };
  if (queue === null || queue.videoId === session.videoId) return { target: session, session };
  if (queueHolds(session.videoId)) return { target: queue, session: null };
  return { target: session, session };
}
